import { useParams, } from 'react-router-dom';
import { useCallback, useMemo } from 'react';
import * as Sentry from '@sentry/react';

import api from '../api/axios';
import authorizationHeader from '../api/token';

const MediBotRole = {
  Evaluation: 'evaluation',
  Question: 'question',
  General: 'general',
}

function useMediBotRole() {
  const path = window.location.pathname;
  // Check for question editor route first (more specific)
  // Route pattern: /courses/:id/:id/evaluations/:id/questions
  if (path.match(/\/courses\/\d+\/\d+\/evaluations\/\d+\/question/)) {
    return MediBotRole.Question;
  } else if (path.match(/\/courses\/\d+\/\d+\/evaluations/)) {
    return MediBotRole.Evaluation;
  }
  return null;
}

export const questionGenerateExplain = async id => {
  const token = await authorizationHeader();
  const question = await api
    .patch(
      `/MediBot/questions/generate-explain/${id}`,
      {},
      {
        headers: token,
      }
    )
    .then(res => res)
    .catch(err => {
      Sentry.captureException(err);
      if (err?.response?.data) {
        Sentry.captureMessage(err.response.data);
      }
      return err;
    });
  return question.data;
};

export const questionGenerateTips = async id => {
  const token = await authorizationHeader();
  const question = await api
    .patch(
      `/MediBot/questions/generate-tips/${id}`,
      {},
      {
        headers: token,
      }
    )
    .then(res => res)
    .catch(err => {
      Sentry.captureException(err);
      if (err?.response?.data) {
        Sentry.captureMessage(err.response.data);
      }
      return err;
    });
  return question.data;
};

// Question Editor API functions
const getChat = async (context) => {
  const token = await authorizationHeader();
  const chat = await api
    .get(`/MediBot/teachers/chat`, {
      headers: token,
      params: context,
    })
    .then(res => res)
    .catch(err => {
      Sentry.captureException(err);
      return err;
    });
  return chat.data;
}

const sendMessage = async (chat_id, message) => {
  const token = await authorizationHeader();
  const chat = await api
    .post(`/MediBot/teachers/message`, {
      chat_id,
      message,
    }, { headers: token })
    .then(res => res)
    .catch(err => {
      Sentry.captureException(err);
      return err;
    });
  return chat.data;
}

const sendMessageStream = async (chat_id, message, onChunk, onComplete, onError) => {
  try {
    const token = await authorizationHeader();
    const response = await fetch(`${api.defaults.baseURL}/MediBot/teachers/message-stream`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...token,
      },
      body: JSON.stringify({
        chat_id,
        message,
      }),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let completeData = null;

    while (true) {
      const { done, value } = await reader.read();

      if (done) {
        break;
      }

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');

      // Keep the last incomplete line in the buffer
      buffer = lines.pop() || '';

      for (const line of lines) {
        if (line.startsWith('data: ')) {
          const data = line.slice(6); // Remove 'data: ' prefix

          if (data === 'DONE') {
            onComplete && onComplete(completeData);
            break;
          } else if (data.startsWith('ERROR:')) {
            const errorMsg = data.slice(7);
            onError && onError(errorMsg);
            break;
          } else {
            try {
              // Try to parse as JSON first
              const parsed = JSON.parse(data);

              // Check if this is a COMPLETE message with full structured data
              if (parsed && typeof parsed === 'object' && parsed.type === 'COMPLETE') {
                completeData = parsed.data;
                // Don't call onChunk for COMPLETE message, just store it
              } else if (typeof parsed === 'string') {
                // It's a text chunk for streaming display
                onChunk && onChunk(parsed);
              } else {
                // Unknown format, pass as is
                onChunk && onChunk(data);
              }
            } catch (e) {
              // If it's not valid JSON, just pass the string
              onChunk && onChunk(data);
            }
          }
        }
      }
    }
  } catch (error) {
    Sentry.captureException(error);
    onError && onError(error.message);
  }
}

export const useMediBot = () => {
  const role = useMediBotRole();
  const params = useParams();
  const course_id = params.id;
  const unit_id = params.unit_id;
  const evaluation_id = params.eval_id;

  // Memoize context to prevent unnecessary rerenders
  const context = useMemo(() => {
    if (role === MediBotRole.Evaluation) {
      return {
        course_id,
        unit_id,
        role: MediBotRole.Evaluation,
      };
    } else if (role === MediBotRole.Question) {
      return {
        course_id,
        unit_id,
        evaluation_id,
        role: MediBotRole.Question,
      };
    } else {
      return {
        role: MediBotRole.General,
      };
    }
  }, [role, course_id, unit_id, evaluation_id]);

  // Memoize functions to prevent unnecessary rerenders
  const getChatMemoized = useCallback(() => getChat(context), [
    role,
    course_id,
    unit_id,
    evaluation_id,
  ]);

  const sendMessageMemoized = useCallback(
    (chat_id, message) => sendMessage(chat_id, message),
    []
  );


  return {
    getChat: () => getChat(context),
    sendMessage: (chat_id, message) => sendMessage(chat_id, message),
    sendMessageStream: (chat_id, message, onChunk, onComplete, onError) =>
      sendMessageStream(chat_id, message, onChunk, onComplete, onError),
    getChatMemoized: getChatMemoized,
    sendMessageMemoized: sendMessageMemoized,
    role,
    context,
  }
};
